import { Gift, Zap, Target, Users, TrendingUp, Clock, CheckCircle2, Lock } from 'lucide-react';

interface PromotionsScreenProps {
  onBack: () => void;
}

export function PromotionsScreen({ onBack }: PromotionsScreenProps) {
  const currentStreak = 5;
  const streakBonus = 2500;

  const dailyBonus = {
    target: 15,
    completed: 8,
    reward: 5000,
    timeLeft: '6h 24m',
  };

  const weeklyBonus = {
    target: 80,
    completed: 52,
    reward: 25000,
    daysLeft: 2,
  };

  const quests = [
    {
      id: 1,
      title: 'Peak Hour Master',
      description: 'Complete 5 deliveries during peak hours (5-8 PM)',
      progress: 3,
      target: 5,
      reward: 3000,
      active: true,
      expiresIn: '8h',
    },
    {
      id: 2,
      title: 'Customer Delight',
      description: 'Maintain 5-star rating for next 10 deliveries',
      progress: 6,
      target: 10,
      reward: 5000,
      active: true,
      expiresIn: '2d',
    },
    {
      id: 3,
      title: 'Speed Demon',
      description: 'Complete 3 deliveries 10 minutes early',
      progress: 1,
      target: 3,
      reward: 2500,
      active: true,
      expiresIn: '12h',
    },
    {
      id: 4,
      title: 'Weekend Warrior',
      description: 'Complete 20 deliveries this weekend',
      progress: 0,
      target: 20,
      reward: 8000,
      active: false,
      startsIn: '2d',
    },
  ];

  const surgeZones = [
    { area: 'Victoria Island', multiplier: 1.8, color: 'text-red-600' },
    { area: 'Lekki Phase 1', multiplier: 1.5, color: 'text-orange-600' },
    { area: 'Ikoyi', multiplier: 1.3, color: 'text-yellow-600' },
  ];

  const referralStats = {
    total: 3,
    active: 2,
    earnings: 15000,
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-br from-[#E0001B]/10 to-[#00B277]/10">
        <button
          onClick={onBack}
          className="mb-6 text-[#E0001B] hover:underline"
        >
          ← Back
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-[28px] text-[#111111] mb-1">
              Bonuses & Quests
            </h1>
            <p className="text-[#6B6B6B]">Earn extra with challenges</p>
          </div>
        </div>

        {/* Streak Card */}
        <div className="p-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-[16px] text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6" />
              <span className="text-[18px] font-medium">Daily Streak</span>
            </div>
            <div className="text-right">
              <p className="text-[28px] font-bold">{currentStreak}</p>
              <p className="text-[12px] opacity-80">days</p>
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            {[1, 2, 3, 4, 5, 6, 7].map(day => (
              <div
                key={day}
                className={`flex-1 h-2 rounded-full ${
                  day <= currentStreak ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
          <p className="text-[14px] opacity-90">
            Keep delivering daily to earn ₦{streakBonus.toLocaleString()}/day bonus
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {/* Daily & Weekly Targets */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-blue-50 rounded-[12px] border border-blue-100">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="text-[14px] text-blue-900 font-medium">Daily Target</span>
            </div>
            <p className="text-[24px] font-bold text-blue-600 mb-1">
              {dailyBonus.completed}/{dailyBonus.target}
            </p>
            <div className="h-2 bg-blue-200 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${(dailyBonus.completed / dailyBonus.target) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-[12px]">
              <span className="text-blue-700">₦{dailyBonus.reward.toLocaleString()}</span>
              <span className="text-blue-600">{dailyBonus.timeLeft} left</span>
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-[12px] border border-green-100">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-[14px] text-green-900 font-medium">Weekly Target</span>
            </div>
            <p className="text-[24px] font-bold text-green-600 mb-1">
              {weeklyBonus.completed}/{weeklyBonus.target}
            </p>
            <div className="h-2 bg-green-200 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-green-600 transition-all"
                style={{ width: `${(weeklyBonus.completed / weeklyBonus.target) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-[12px]">
              <span className="text-green-700">₦{weeklyBonus.reward.toLocaleString()}</span>
              <span className="text-green-600">{weeklyBonus.daysLeft} days left</span>
            </div>
          </div>
        </div>

        {/* Active Quests */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">Active Quests</h2>
          <div className="space-y-3">
            {quests.filter(q => q.active).map(quest => (
              <div
                key={quest.id}
                className="p-4 bg-white border-2 border-purple-200 rounded-[12px]"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-[16px] font-medium mb-1">{quest.title}</h3>
                    <p className="text-[14px] text-[#6B6B6B]">{quest.description}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-[18px] font-bold text-[#00B277]">
                      ₦{quest.reward.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="h-2 bg-purple-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-600 transition-all"
                        style={{ width: `${(quest.progress / quest.target) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-[14px] text-[#6B6B6B] whitespace-nowrap">
                    {quest.progress}/{quest.target}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-[12px] text-orange-600">Expires in {quest.expiresIn}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Quests */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">Upcoming Quests</h2>
          <div className="space-y-3">
            {quests.filter(q => !q.active).map(quest => (
              <div
                key={quest.id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-[12px] opacity-60"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Lock className="w-5 h-5 text-[#6B6B6B] mt-1" />
                    <div>
                      <h3 className="text-[16px] font-medium mb-1">{quest.title}</h3>
                      <p className="text-[14px] text-[#6B6B6B]">{quest.description}</p>
                      <p className="text-[12px] text-[#6B6B6B] mt-2">Starts in {quest.startsIn}</p>
                    </div>
                  </div>
                  <p className="text-[16px] font-bold text-[#6B6B6B]">
                    ₦{quest.reward.toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Surge Zones */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">🔥 Surge Pricing Now</h2>
          <div className="space-y-2">
            {surgeZones.map((zone, index) => (
              <div
                key={index}
                className="p-4 bg-white border border-gray-200 rounded-[12px] flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Zap className={`w-5 h-5 ${zone.color}`} />
                  <span className="text-[14px] font-medium">{zone.area}</span>
                </div>
                <span className={`text-[18px] font-bold ${zone.color}`}>
                  {zone.multiplier}x
                </span>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-3">
            * Surge pricing updates every 15 minutes based on demand
          </p>
        </div>

        {/* Referral Program */}
        <div className="p-6 bg-gradient-to-br from-[#E0001B]/10 to-[#00B277]/10 rounded-[12px] border border-gray-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#E0001B] flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-[18px] font-medium mb-1">Referral Program</h3>
              <p className="text-[14px] text-[#6B6B6B]">
                Earn ₦5,000 for each rider you refer
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
              <p className="text-[24px] font-bold text-[#E0001B]">{referralStats.total}</p>
              <p className="text-[12px] text-[#6B6B6B]">Total Referrals</p>
            </div>
            <div className="text-center">
              <p className="text-[24px] font-bold text-[#00B277]">{referralStats.active}</p>
              <p className="text-[12px] text-[#6B6B6B]">Active</p>
            </div>
            <div className="text-center">
              <p className="text-[24px] font-bold text-[#111111]">
                ₦{(referralStats.earnings / 1000).toFixed(0)}k
              </p>
              <p className="text-[12px] text-[#6B6B6B]">Earned</p>
            </div>
          </div>

          <div className="p-3 bg-white rounded-[12px] flex items-center justify-between">
            <div>
              <p className="text-[12px] text-[#6B6B6B] mb-0.5">Your referral code</p>
              <p className="text-[20px] font-bold text-[#111111]">GO7-RDR-4287</p>
            </div>
            <button className="px-4 py-2 bg-[#E0001B] text-white rounded-[12px] text-[14px] hover:bg-[#C00017] transition-colors">
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
